import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { STATUS_COLORS, PLATFORM_META } from "@/lib/constants";
import { aptPinSvg, sitePinSvg } from "@/lib/pin";

function spreadPoints(points) {
  const groups = {};
  points.forEach((p) => {
    const key = `${p.latitude.toFixed(5)},${p.longitude.toFixed(5)}`;
    (groups[key] = groups[key] || []).push(p);
  });
  const out = [];
  Object.values(groups).forEach((members) => {
    if (members.length === 1) { out.push(members[0]); return; }
    const n = members.length;
    const radius = 0.00035;
    members.forEach((p, i) => {
      const angle = (2 * Math.PI * i) / n - Math.PI / 2;
      out.push({
        ...p,
        displayLat: p.latitude + radius * Math.sin(angle),
        displayLng: p.longitude + (radius / Math.cos((p.latitude * Math.PI) / 180)) * Math.cos(angle),
      });
    });
  });
  return out;
}

function ZoomWatcher({ onZoom }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google || !onZoom) return;
    let l = null;
    try {
      onZoom(map.getZoom());
      l = window.google.maps.event.addListener(map, "zoom_changed", () => onZoom(map.getZoom()));
    } catch (e) { /* zoom indisponible */ }
    return () => { try { if (l) window.google.maps.event.removeListener(l); } catch (e) {} };
  }, [map]);
  return null;
}

function SiteHoverCard({ s }) {
  const isHq = s.type === "hq";
  return (
    <div data-testid={`site-hover-card-${s.id}`}
         className="relative w-52 bg-white rounded-xl shadow-xl border border-slate-200 px-3 py-2.5 pointer-events-none">
      <div className="text-sm font-bold text-slate-900 leading-tight truncate">{s.name}</div>
      <div className="text-xs font-semibold mt-0.5" style={{ color: isHq ? "#B8860B" : "#0E7490" }}>
        {isHq ? "QG — Bureau général" : `Bureau — ${s.cleaning_company || "ménage"}`}
      </div>
      <div className="text-xs text-slate-500 mt-0.5 truncate">{s.address}{s.city ? `, ${s.city}` : ""}</div>
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[7px] w-3 h-3 bg-white border-b border-r border-slate-200 rotate-45" />
    </div>
  );
}

function HoverCard({ a }) {
  const status = STATUS_COLORS[a.color_status] || STATUS_COLORS.ok;
  const plats = (a.platforms || []).map((p) => p.platform);
  const platLabel = plats.length ? plats.map((k) => PLATFORM_META[k]?.name || k).join(" + ") : "Aucune plateforme";
  const dept = a.postal_code ? String(a.postal_code).slice(0, 2) : "";
  return (
    <div data-testid={`map-hover-card-${a.id}`}
         className="relative w-52 bg-white rounded-xl shadow-xl border border-slate-200 px-3 py-2.5 pointer-events-none">
      <div className="text-sm font-bold text-slate-900 leading-tight truncate">{a.name}</div>
      <div className="text-xs text-slate-500 mt-0.5">{a.city}{dept ? ` (${dept})` : ""}</div>
      <div className="flex items-center gap-1.5 text-xs mt-1.5">
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: status.hex }} />
        <span className="text-slate-700 truncate">{status.label} • {a.cleaning_company}</span>
      </div>
      <div className="text-xs text-slate-600 mt-1 truncate">{platLabel}</div>
      <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-[7px] w-3 h-3 bg-white border-b border-r border-slate-200 rotate-45" />
    </div>
  );
}

function Markers({ points, sites = [], onSelect, onSelectSite, overlayEl }) {
  const map = useMap();
  const clusterer = useRef(null);
  const markers = useRef({});
  const [hover, setHover] = useState(null);
  const closeTimer = useRef(null);
  const spread = useMemo(() => spreadPoints(points), [points]);

  const pinPos = (id, kind) => {
    const el = document.querySelector(`[data-testid="${kind === "apt" ? "gmap-marker" : "gmap-site"}-${id}"]`);
    if (!el || !overlayEl) return null;
    const r = el.getBoundingClientRect();
    const c = overlayEl.getBoundingClientRect();
    return { x: r.left - c.left + r.width / 2, y: r.top - c.top };
  };

  const openHover = useCallback((kind, id, data) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    const pos = pinPos(id, kind);
    if (pos) setHover({ kind, id, data, ...pos });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlayEl]);

  const scheduleClose = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setHover(null), 120);
  }, []);

  useEffect(() => {
    if (!hover || !map || !window.google) return undefined;
    const l = window.google.maps.event.addListener(map, "bounds_changed", () => {
      const pos = pinPos(hover.id, hover.kind);
      if (pos) setHover((h) => (h ? { ...h, ...pos } : null));
      else setHover(null);
    });
    return () => { try { window.google.maps.event.removeListener(l); } catch (e) { /* ignoré */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hover?.id, map]);

  const safeRender = () => {
    try {
      clusterer.current?.render?.();
    } catch (e) { /* rendu ignoré */ }
  };

  const syncClusterer = useCallback(() => {
    const c = clusterer.current;
    if (!c || !map || !window.google) return;
    try {
      c.clearMarkers();
      c.addMarkers(Object.values(markers.current));
      c.render();
    } catch (e) { /* synchronisation ignorée */ }
  }, [map]);

  useEffect(() => {
    if (!map || !window.google) return;
    let cancelled = false;
    let onIdle = null;
    let onceA = null;
    let onceB = null;
    const init = () => {
      if (cancelled || clusterer.current) return;
      clusterer.current = new MarkerClusterer({ map, maxZoom: 15 });
      try { onIdle = window.google.maps.event.addListener(map, "idle", safeRender); } catch (e) {}
      syncClusterer();
    };
    let ready = false;
    try { ready = !!map.getProjection?.(); } catch (e) {}
    if (ready) {
      init();
    } else {
      try {
        const evt = window.google.maps.event;
        onceA = evt.addListenerOnce(map, "projection_changed", init);
        onceB = evt.addListenerOnce(map, "idle", init);
      } catch (e) {}
    }
    return () => {
      cancelled = true;
      try {
        const evt = window.google.maps.event;
        if (onceA) evt.removeListener(onceA);
        if (onceB) evt.removeListener(onceB);
        if (onIdle) evt.removeListener(onIdle);
      } catch (e) {}
      const c = clusterer.current;
      clusterer.current = null;
      try { c?.clearMarkers(); c?.setMap(null); } catch (e) {}
    };
  }, [map]);

  useEffect(() => {
    syncClusterer();
  }, [map, spread, sites]);

  const setRef = useCallback((m, id) => {
    if (m) markers.current[id] = m;
    else delete markers.current[id];
    syncClusterer();
  }, [syncClusterer]);

  const aptMarkers = useMemo(() => spread.map((a) => (
    <AdvancedMarker key={a.id} position={{ lat: a.displayLat ?? a.latitude, lng: a.displayLng ?? a.longitude }}
                    ref={(m) => setRef(m, a.id)} onClick={() => onSelect(a)}
                    zIndex={1}>
      <div className="relative" onMouseEnter={() => openHover("apt", a.id, a)} onMouseLeave={scheduleClose}>
        <div className="cursor-pointer transition-transform duration-150 hover:scale-110"
             data-testid={`gmap-marker-${a.id}`}
             dangerouslySetInnerHTML={{ __html: aptPinSvg(a, a.id) }} />
      </div>
    </AdvancedMarker>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  )), [spread, onSelect, openHover, scheduleClose, setRef]);

  const siteMarkers = useMemo(() => sites.map((s) => (
    <AdvancedMarker key={s.id} position={{ lat: s.latitude, lng: s.longitude }}
                    ref={(m) => setRef(m, s.id)} onClick={() => onSelectSite?.(s)}
                    zIndex={2}>
      <div className="relative" onMouseEnter={() => openHover("site", s.id, s)} onMouseLeave={scheduleClose}>
        <div className="cursor-pointer transition-transform duration-150 hover:scale-110"
             data-testid={`gmap-site-${s.id}`}
             dangerouslySetInnerHTML={{ __html: sitePinSvg(s) }} />
      </div>
    </AdvancedMarker>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  )), [sites, onSelectSite, openHover, scheduleClose, setRef]);

  return (
    <>
      {aptMarkers}
      {siteMarkers}
      {hover && overlayEl && createPortal(
        <div className="absolute pointer-events-none"
             style={{ left: hover.x, top: hover.y, transform: "translate(-50%, -100%) translateY(-8px)", zIndex: 30 }}>
          {hover.kind === "apt" ? <HoverCard a={hover.data} /> : <SiteHoverCard s={hover.data} />}
        </div>,
        overlayEl
      )}
    </>
  );
}

function FitBounds({ points }) {
  const map = useMap();
  const lastKey = useRef(null);
  useEffect(() => {
    if (!map || !points.length || !window.google) return;
    const key = points.map((p) => p.id).join(",");
    if (lastKey.current === key) return;
    lastKey.current = key;
    const b = new window.google.maps.LatLngBounds();
    points.forEach((p) => b.extend({ lat: p.displayLat ?? p.latitude, lng: p.displayLng ?? p.longitude }));
    map.fitBounds(b, 60);
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > 17) map.setZoom(17);
    });
  }, [map, points]);
  return null;
}

export default function GoogleAptMap({ points, sites = [], onSelect, onSelectSite, onZoomChange }) {
  const [mapType, setMapType] = useState("roadmap");
  const [overlayEl, setOverlayEl] = useState(null);
  return (
    <div className="absolute inset-0">
      <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
        <Map defaultCenter={{ lat: 48.8566, lng: 2.3522 }} defaultZoom={12} mapId="DEMO_MAP_ID"
             mapTypeId={mapType} gestureHandling="greedy" disableDefaultUI zoomControl
             style={{ width: "100%", height: "100%" }}>
          <Markers points={points} sites={sites} onSelect={onSelect} onSelectSite={onSelectSite} overlayEl={overlayEl} />
          <ZoomWatcher onZoom={onZoomChange} />
          <FitBounds points={points} />
        </Map>
      </APIProvider>
      <div ref={setOverlayEl} className="absolute inset-0 pointer-events-none z-20" data-testid="hover-overlay" />
      <button onClick={() => setMapType((m) => (m === "roadmap" ? "hybrid" : "roadmap"))}
              data-testid="map-type-toggle"
              className="absolute top-16 right-3 z-[1000] bg-white rounded-lg shadow-md border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
        {mapType === "roadmap" ? "Satellite" : "Plan"}
      </button>
    </div>
  );
}
